import { toaster } from '@ui/components/Toaster';
import { paths } from '@ui/config/paths';
import { useUploadImage } from '@ui/hooks/api/useImage';
import { useAgentTokenLaunch } from '@ui/hooks/bondingCurve/useAgentTokenLaunch';
import { useRouterWithI18n } from '@ui/locales/browserSSG';
import { useFormContext } from 'react-hook-form';

import type { CreateAgentFormValues } from './constants';
import { useCreateAgentModal } from './useCreateAgentModal';

export const useFinalizePurchase = () => {
  const router = useRouterWithI18n();
  const formValues = useFormContext<CreateAgentFormValues>().getValues();
  const { formState } = useFormContext();
  const buyAmount = useCreateAgentModal((s) => s.buyAmount);
  const { isLoading: isUploadImageLoading, mutateAsync: uploadImage } =
    useUploadImage();

  const { isLoading: isLaunchLoading, mutateAsync: launchToken } =
    useAgentTokenLaunch(({ tokenAddress, hash }) => {
      if (!tokenAddress) {
        console.error('error creating agent, no address');
        return;
      }
      useCreateAgentModal.getState().closeModal();
      router.push(paths.agent(tokenAddress));
      toaster.create({
        type: 'success',
        title: '(＾▽＾) Agent created!',
        description: 'Give your AI a standing ovation!',
        meta: {
          txHash: hash,
        },
      });
    });

  const isLoading = isLaunchLoading || isUploadImageLoading;
  const disabled = isLoading || buyAmount.lte(0) || !formState.isValid;

  const handleClick = async () => {
    const image = formValues.image;
    if (!image) throw new Error('should not call without set image');
    const imageUrl = await uploadImage({ file: image });
    try {
      await launchToken({
        name: formValues.name,
        ticker: formValues.ticker,
        description: formValues.description,
        image: imageUrl,
        urls: [
          formValues.twitter,
          formValues.telegram,
          formValues.youtube,
          formValues.website,
        ],
        purchaseAmount: buyAmount,
      });
    } catch (e: unknown) {
      toaster.create({
        type: 'error',
        title: '(〒︿〒) Something went wrong!',
        description: "Maybe it was a stage fright moment, let's try again!",
      });
    }
  };

  return {
    handleClick,
    isLoading,
    disabled,
  };
};
